import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import api from "../services/api";

// Preset amount options in dollars
const PRESET_AMOUNTS = [5, 10, 25, 50, 100];

// Points per dollar conversion rate (100 points = $1)
const POINTS_PER_DOLLAR = 100;

// Fetch user's points balance
async function fetchBalance(): Promise<number> {
  const response = await api.get<{ success: boolean; data: { balance: number } }>(
    "/deposits/balance"
  );
  return response.data.data.balance;
}

interface StripeCheckoutResponse {
  success: boolean;
  data: {
    checkout_url: string;
    session_id: string;
  };
}

async function createStripeCheckout(amount: number): Promise<StripeCheckoutResponse> {
  const response = await api.post<StripeCheckoutResponse>("/deposits/stripe", {
    amount,
  });
  return response.data;
}

export default function DepositPage() {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();

  // Confirm payment mutation (client-side backup if webhook fails)
  const confirmMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await api.post("/deposits/stripe/confirm-session", {
        sessionId: sessionId,
      });
      return response.data;
    },
    onSuccess: () => {
      setSuccessMessage("Payment confirmed! Your points have been added.");
      queryClient.invalidateQueries({ queryKey: ["points", "balance"] });
    },
    onError: (err: any) => {
      // Log the actual error for debugging
      console.error("[Deposit] Confirmation error:", err);
      const errorMsg = err.response?.data?.error || err.message;
      console.error("[Deposit] Error details:", errorMsg);
      // Show error to user so we can diagnose
      setError(`Payment confirmation failed: ${errorMsg}. Webhook may still process it.`);
      queryClient.invalidateQueries({ queryKey: ["points", "balance"] });
    },
  });

  // Handle Stripe return after payment
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const success = searchParams.get("success");

    if (sessionId && success === "true") {
      // Clear URL params
      setSearchParams({});
      // Try to confirm client-side (webhook is the primary method)
      confirmMutation.mutate(sessionId);
    } else if (searchParams.get("cancelled") === "true") {
      setSearchParams({});
      setError("Payment was cancelled.");
    }
  }, [searchParams, setSearchParams, queryClient, confirmMutation]);

  // Fetch user's points balance
  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ["points", "balance"],
    queryFn: fetchBalance,
  });

  // Stripe checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: (amount: number) => createStripeCheckout(amount),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      if (data.data.checkout_url) {
        window.location.href = data.data.checkout_url;
      }
    },
    onError: (err: any) => {
      setError(
        err.response?.data?.error ||
        err.message ||
        "Failed to initiate payment. Please try again."
      );
    },
  });

  // Get the effective amount (either preset or custom)
  const effectiveAmount = customAmount
    ? parseFloat(customAmount)
    : selectedAmount;

  // Calculate points to be added
  const pointsToAdd = effectiveAmount ? effectiveAmount * POINTS_PER_DOLLAR : 0;

  // Validate custom amount
  const isCustomAmountValid = customAmount
    ? parseFloat(customAmount) >= 1 && parseFloat(customAmount) <= 1000
    : true;

  const handlePresetClick = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
    setError(null);
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(null);
    setError(null);
  };

  const handlePay = () => {
    if (!effectiveAmount || effectiveAmount < 1) {
      setError("Please select or enter an amount of at least $1");
      return;
    }
    if (effectiveAmount > 1000) {
      setError("Maximum deposit amount is $1,000");
      return;
    }
    setError(null);
    checkoutMutation.mutate(effectiveAmount);
  };

  return (
    <div>
      {/* Header Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-100 mb-2">Add Points</h1>
        <p className="text-gray-400">
          Purchase points to fund surveys and reward respondents
        </p>
      </div>

      {/* Current Balance Card */}
      <div className="card mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-gray-400 mb-1">Current Points Balance</p>
            {balanceLoading ? (
              <div className="animate-pulse bg-dark-700 h-10 w-32 rounded"></div>
            ) : (
              <p className="text-4xl font-bold text-primary-300">
                {balance?.toLocaleString() ?? 0}
                <span className="text-lg font-normal text-gray-400 ml-2">points</span>
              </p>
            )}
          </div>
          <Link to="/redeem" className="btn bg-dark-600 hover:bg-dark-500 text-gray-300">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Redeem Points
          </Link>
        </div>
      </div>

      {/* Deposit Section */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-100 mb-6">Select Amount</h2>

        {/* Preset Amount Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {PRESET_AMOUNTS.map((amount) => (
            <button
              key={amount}
              onClick={() => handlePresetClick(amount)}
              className={`py-4 px-6 rounded-lg font-semibold transition-all ${
                selectedAmount === amount
                  ? "bg-primary-500 text-white ring-2 ring-primary-400 ring-offset-2 ring-offset-dark-800"
                  : "bg-dark-700 text-gray-300 hover:bg-dark-600 hover:text-gray-100"
              }`}
            >
              ${amount}
            </button>
          ))}
        </div>

        {/* Custom Amount Input */}
        <div className="mb-6">
          <label htmlFor="customAmount" className="block text-sm font-medium text-gray-300 mb-2">
            Or enter a custom amount
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
            <input
              id="customAmount"
              type="number"
              min="1"
              max="1000"
              step="1"
              className="input pl-8"
              placeholder="Enter amount (min $1, max $1000)"
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
            />
          </div>
          {customAmount && !isCustomAmountValid && (
            <p className="text-sm text-red-400 mt-2">
              Amount must be between $1 and $1,000
            </p>
          )}
        </div>

        {/* Summary */}
        {effectiveAmount && effectiveAmount > 0 && isCustomAmountValid && (
          <div className="bg-dark-700 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">You will receive</p>
                <p className="text-2xl font-bold text-gray-100">
                  {pointsToAdd.toLocaleString()} points
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">Total charge</p>
                <p className="text-2xl font-bold text-gray-100">${effectiveAmount.toFixed(2)}</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              100 points = $1.00 USD
            </p>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 text-green-400">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3 text-red-400">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Stripe Integration Notice */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3 text-blue-400">
            <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm">
              Payment is securely processed through Stripe. You will be redirected to complete your purchase.
            </p>
          </div>
        </div>

        {/* Pay Button */}
        <button
          onClick={handlePay}
          disabled={checkoutMutation.isPending || !effectiveAmount || effectiveAmount < 1 || !isCustomAmountValid}
          className="btn btn-primary w-full py-3 text-lg font-semibold"
        >
          {checkoutMutation.isPending ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </span>
          ) : (
            <>
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              Pay {effectiveAmount && effectiveAmount > 0 ? `$${effectiveAmount.toFixed(2)}` : ""}
            </>
          )}
        </button>

        {/* Footer info */}
        <p className="text-xs text-gray-500 text-center mt-4">
          By purchasing points, you agree to our terms of service. Points are non-transferable and non-refundable.
        </p>
      </div>
    </div>
  );
}
